// lib/tracking/page-context.ts
// Page context carried on every affiliate_click: page_path, page_type, slug.
// Paths only, never query strings or PII. Unknown values are the explicit
// string "unknown", never omitted.

import { normalizeHost } from "@/lib/tracking/analytics-host";
import { readGoSource, sanitizeSourcePath } from "@/lib/go-source";

export const UNKNOWN_PAGE_VALUE = "unknown";

export type PageType =
  | "review"
  | "guide"
  | "verdict"
  | "tool"
  | "category"
  | "home"
  | "other"
  | typeof UNKNOWN_PAGE_VALUE;

export interface PageContext {
  page_path: string;
  page_type: PageType;
  slug: string;
}

const UNKNOWN_CONTEXT: PageContext = {
  page_path: UNKNOWN_PAGE_VALUE,
  page_type: UNKNOWN_PAGE_VALUE,
  slug: UNKNOWN_PAGE_VALUE,
};

const SEGMENT_TYPES: Record<string, PageType> = {
  reviews: "review",
  guides: "guide",
  verdicts: "verdict",
  tools: "tool",
  categories: "category",
};

/** Derive the context from a site path. Anything unsafe becomes "unknown". */
export function derivePageContext(rawPath: string | null | undefined): PageContext {
  const path = sanitizeSourcePath(rawPath);
  if (!path) return { ...UNKNOWN_CONTEXT };
  const segments = path.split("/").filter(Boolean);
  if (segments.length === 0) return { page_path: "/", page_type: "home", slug: "home" };
  return {
    page_path: path,
    page_type: SEGMENT_TYPES[segments[0]] ?? "other",
    slug: segments[segments.length - 1],
  };
}

/** Path of a same-site Referer, or null. Query string and fragment are dropped. */
function sameSiteRefererPath(referer: string | null | undefined, requestHost: string | null): string | null {
  if (!referer) return null;
  try {
    const url = new URL(referer);
    const host = normalizeHost(url.host);
    if (!host || host !== normalizeHost(requestHost)) return null;
    return url.pathname;
  } catch {
    return null;
  }
}

/**
 * Server-side context for a /go or /api/route request: the validated ?src=
 * path first, then the same-site Referer path, else "unknown".
 */
export function pageContextFromRequest(request: {
  headers: Pick<Headers, "get">;
  nextUrl: { searchParams: Pick<URLSearchParams, "get">; hostname: string };
}): PageContext {
  const src = readGoSource(request.nextUrl.searchParams);
  if (src) return derivePageContext(src);
  const host =
    request.headers.get("x-forwarded-host") ?? request.headers.get("host") ?? request.nextUrl.hostname;
  return derivePageContext(sameSiteRefererPath(request.headers.get("referer"), host));
}

/** Page context for a path-like value from a client beacon (untrusted). */
export function pageContextFromClientPath(raw: unknown): PageContext {
  return derivePageContext(typeof raw === "string" ? raw : null);
}
